import type {
  ChatMessage,
  ChatMessageContent,
} from "schemas/generated/chatMessage";

import { create } from "zustand";

interface ChatStore {
  isChatLoading: boolean;
  messages: ChatMessage[];
  viewingVersionId: string | null;
  actions: {
    setIsChatLoading: (isChatLoading: boolean) => void;
    // Replace the generic setMessages with specific actions
    setMessages: (messages: ChatMessage[]) => void; // For server sync
    clearMessages: () => void;
    addMessages: (messages: ChatMessage[]) => void;
    addOrUpdateMessage: (
      messageId: string,
      content: ChatMessageContent,
      role: "assistant" | "tool",
      chatId: string,
    ) => void;
    setViewingVersionId: (viewingVersionId: string | null) => void;
  };
}

const useChatStore = create<ChatStore>((set) => ({
  isChatLoading: false,
  messages: [],
  viewingVersionId: null,
  actions: {
    setIsChatLoading: (isChatLoading: boolean) => set({ isChatLoading }),
    setMessages: (messages: ChatMessage[]) => set({ messages }),
    clearMessages: () => set({ messages: [] }),
    addMessages: (newMessages: ChatMessage[]) =>
      set((state) => ({ messages: [...state.messages, ...newMessages] })),
    addOrUpdateMessage: (messageId, content, role, chatId) =>
      set((state) => {
        const existingMessage = state.messages.find(
          (msg) => msg.id === messageId,
        );

        if (existingMessage) {
          return {
            messages: state.messages.map((msg) =>
              msg.id === messageId
                ? { ...msg, content, createdAt: new Date() }
                : msg,
            ),
          };
        } else {
          return {
            messages: [
              ...state.messages,
              {
                id: messageId,
                content,
                createdAt: new Date(),
                role,
                chatId,
              },
            ],
          };
        }
      }),
    setViewingVersionId: (viewingVersionId: string | null) =>
      set({ viewingVersionId }),
  },
}));

export const useIsChatLoading = () =>
  useChatStore((state) => state.isChatLoading);

export const useMessages = () => useChatStore((state) => state.messages);

export const useViewingVersionId = () =>
  useChatStore((state) => state.viewingVersionId);

// NOTE (Bobby: 2025-07-09): Creating just one selector for all actions since actions are static and never change
// so regardless they don't cause any re-renders or impact performance.
export const useChatStoreActions = () => useChatStore((state) => state.actions);
